/** Custom field definitions: keys, option lists, value coercion, display and filtering. */

import type { CustomFieldDef, CustomFieldType, CustomFieldValue, CustomFieldValues } from './types'

export const CUSTOM_FIELD_TYPES: ReadonlyArray<{ value: CustomFieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'select', label: 'Single choice' },
  { value: 'multi_select', label: 'Multiple choice' },
  { value: 'url', label: 'Link' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
]

const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/

export function isValidKey(key: string): boolean {
  return KEY_RE.test(key)
}

/** "Beat / topic" becomes "beat_topic". Always starts with a letter. */
export function slugifyKey(label: string): string {
  let k = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!k) return ''
  if (!/^[a-z]/.test(k)) k = `f_${k}`
  return k.slice(0, 40).replace(/_+$/, '')
}

export function hasOptions(type: CustomFieldType): boolean {
  return type === 'select' || type === 'multi_select'
}

/** One option per line (or comma separated) from a textarea. */
export function parseOptions(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of text.split(/[\n,]/)) {
    const t = part.trim()
    if (t && !seen.has(t.toLowerCase())) {
      seen.add(t.toLowerCase())
      out.push(t)
    }
  }
  return out
}

export interface DefDraft {
  entity: 'contact' | 'account'
  key: string
  label: string
  type: CustomFieldType
  options: string[]
}

/** Problem with a definition draft, or null. `existingKeys` are the other keys for the same entity. */
export function validateDef(draft: DefDraft, existingKeys: readonly string[]): string | null {
  if (!draft.label.trim()) return 'Give the field a name.'
  if (draft.label.trim().length > 80) return 'The name is too long (80 characters at most).'
  if (!isValidKey(draft.key)) {
    return 'The key must start with a letter and use lower-case letters, digits and underscores (40 at most).'
  }
  if (existingKeys.includes(draft.key))
    return `A ${draft.entity} field with key "${draft.key}" already exists.`
  if (hasOptions(draft.type) && draft.options.length === 0) return 'Add at least one choice.'
  return null
}

/** Turn what a form control produced into the stored value, or an error string. */
export function coerceValue(
  def: Pick<CustomFieldDef, 'type' | 'options' | 'label'>,
  raw: unknown,
): { ok: true; value: CustomFieldValue } | { ok: false; error: string } {
  const empty =
    raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)
  if (empty) return { ok: true, value: null }

  switch (def.type) {
    case 'text':
      return { ok: true, value: String(raw).trim() || null }
    case 'url': {
      const s = String(raw).trim()
      try {
        const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`)
        if (u.protocol !== 'http:' && u.protocol !== 'https:') throw new Error('scheme')
        return { ok: true, value: u.toString() }
      } catch {
        return { ok: false, error: `${def.label}: enter a valid web address.` }
      }
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
      return Number.isFinite(n)
        ? { ok: true, value: n }
        : { ok: false, error: `${def.label}: enter a number.` }
    }
    case 'date': {
      const s = String(raw).trim()
      return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`))
        ? { ok: true, value: s }
        : { ok: false, error: `${def.label}: use the date picker (yyyy-mm-dd).` }
    }
    case 'select': {
      const s = String(raw)
      return def.options.includes(s)
        ? { ok: true, value: s }
        : { ok: false, error: `${def.label}: "${s}" is not one of the choices.` }
    }
    case 'multi_select': {
      const arr = (Array.isArray(raw) ? raw : String(raw).split(','))
        .map((x) => String(x).trim())
        .filter(Boolean)
      const bad = arr.find((x) => !def.options.includes(x))
      if (bad) return { ok: false, error: `${def.label}: "${bad}" is not one of the choices.` }
      return { ok: true, value: arr.length > 0 ? arr : null }
    }
  }
}

export function formatValue(
  def: Pick<CustomFieldDef, 'type'>,
  value: CustomFieldValue | undefined,
): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (def.type === 'date' && typeof value === 'string') {
    const d = new Date(`${value}T00:00:00Z`)
    return Number.isNaN(d.getTime())
      ? value
      : d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
  }
  return String(value)
}

/** Keep only values whose key still has a definition; used before saving. */
export function pickDefinedValues(
  defs: readonly CustomFieldDef[],
  values: CustomFieldValues,
): CustomFieldValues {
  const out: CustomFieldValues = {}
  for (const d of defs) {
    if (d.key in values) out[d.key] = values[d.key] ?? null
  }
  return out
}

/** Only the keys whose value changed, with null for a cleared field (the RPC removes null keys). */
export function diffValues(before: CustomFieldValues, after: CustomFieldValues): CustomFieldValues {
  const patch: CustomFieldValues = {}
  for (const key of Object.keys(after)) {
    const a = after[key] ?? null
    const b = before[key] ?? null
    if (JSON.stringify(a) !== JSON.stringify(b)) patch[key] = a
  }
  return patch
}

/**
 * Object for `.contains('custom_fields', obj)` (jsonb @>) that matches contacts
 * whose field equals `value` (or whose multi-choice list includes it).
 */
export function containsFilter(
  def: Pick<CustomFieldDef, 'type' | 'key'>,
  value: string,
): Record<string, CustomFieldValue> | null {
  const v = value.trim()
  if (!v) return null
  switch (def.type) {
    case 'multi_select':
      return { [def.key]: [v] }
    case 'number': {
      const n = Number(v)
      return Number.isFinite(n) ? { [def.key]: n } : null
    }
    default:
      return { [def.key]: v }
  }
}

/** CSV column mapping value for a custom field. */
export const CUSTOM_MAP_PREFIX = 'custom:'

export function customMapOption(def: Pick<CustomFieldDef, 'key'>): string {
  return `${CUSTOM_MAP_PREFIX}${def.key}`
}

export function customKeyFromMap(mapKey: string): string | null {
  return mapKey.startsWith(CUSTOM_MAP_PREFIX) ? mapKey.slice(CUSTOM_MAP_PREFIX.length) : null
}

/**
 * Split an imported row into regular columns and custom field values.
 * Values are coerced per definition; invalid cells are dropped and reported.
 */
export function extractCustomFromRow(
  row: Record<string, unknown>,
  defsByKey: Record<string, CustomFieldDef>,
): { rest: Record<string, unknown>; custom: CustomFieldValues; errors: string[] } {
  const rest: Record<string, unknown> = {}
  const custom: CustomFieldValues = {}
  const errors: string[] = []
  for (const [k, v] of Object.entries(row)) {
    const key = customKeyFromMap(k)
    if (key === null) {
      rest[k] = v
      continue
    }
    const def = defsByKey[key]
    if (!def) continue
    const res = coerceValue(def, v)
    if (res.ok) {
      if (res.value !== null) custom[key] = res.value
    } else {
      errors.push(res.error)
    }
  }
  return { rest, custom, errors }
}
